import { Badge, Divider, Group, JsonInput, Paper, rem, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { InfiniteQuerySelect } from "shared/components/global/InfiniteQuerySelect";
import { useIngestRuleFormContext } from "shared/components/ingest/context";
import { IngestRuleTemplateCard } from "shared/components/ingest/IngestRuleFormTemplateCard";
import { useFormState } from "shared/hooks/useFormState";
import { catching, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { trimChr } from "shared/utils/string";
import { graphql } from "src/gql";

export const IngestRuleForm = () => {
    const form = useIngestRuleFormContext();
    const [samplePayload, setSamplePayload] = useState("");
    const [destinationTemplateId, setDestinationTemplateId] = useFormState(form, "destinationTemplateId");
    const [destinationTemplateData, setDestinationTemplatePreview] = useState<{ subject: string; body: string }>();

    function testSamplePayload(el: object, ks: string[]): boolean {
        if (form.values.matchExpr == "") return false;

        const [k, ...rest] = ks;
        const cur = el[k as keyof typeof el];
        if (rest.length == 0)
            return catching(() => new RegExp(form.values.matchExpr).test(trimChr(JSON.stringify(cur) ?? "", '"'))) ?? false;
        else return testSamplePayload(cur, rest);
    }

    const samplePayloadMatches =
        maybe(catching(() => JSON.parse(samplePayload)))?.take((it: object) =>
            testSamplePayload(it, trimChr(form.values.matchKey, ".").split(".")),
        ) ?? false;

    return (
        <Stack>
            <TextInput label="Key Path" {...form.getInputProps("matchKey")} placeholder="data.item" />
            <TextInput label="Value Regex" {...form.getInputProps("matchExpr")} leftSection={"/"} rightSection={"/"} placeholder="[A-Z]" />

            <Divider mx="-md" />
            <Stack gap="xs">
                <Group justify="space-between" w="100%">
                    <Text size="xs">Test Payload</Text>

                    {samplePayloadMatches ? (
                        <Badge variant="light" color="green">
                            Matches
                        </Badge>
                    ) : (
                        <Badge variant="default">No Match</Badge>
                    )}
                </Group>
                <JsonInput
                    miw={rem(512)}
                    value={samplePayload}
                    onChange={setSamplePayload}
                    formatOnBlur
                    placeholder={JSON.stringify({ payload: { data: "here" } })}
                    autosize
                    minRows={4}
                    validationError={"Invalid JSON"}
                />
            </Stack>

            <Divider mx="-md" />
            <Text fw="bold">Destination Template</Text>

            <InfiniteQuerySelect
                query={query.templates}
                toIdentity={to("id")}
                render={(it) => <IngestRuleTemplateCard {...it.message} />}
                initialValue={destinationTemplateId}
                onChange={(id, item) => {
                    setDestinationTemplateId(id);
                    setDestinationTemplatePreview(item?.message);
                }}
            />

            {maybe(destinationTemplateData)?.take((it) => (
                <Paper withBorder p="sm" maw={rem(512)}>
                    <IngestRuleTemplateCard {...it} />
                </Paper>
            ))}
        </Stack>
    );
};

const query = {
    templates: createOffsetBasedGqlQueryOptions({
        queryKey: qk("support", "ingest-rules", "templates"),
        select: to("templates"),
        document: graphql(`
            query SupportIngestTemplates($limit: Int!, $offset: Int!) {
                templates(limit: $limit, offset: $offset) {
                    id
                    message {
                        subject
                        body
                    }
                }
            }
        `),
    }),
};
